import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import type { PublicProfileDto } from '@redpandev/shared';
import { Mascot, Spinner } from '@redpandev/ui';
import { api, ApiError } from '../lib/api';
import { StatsBlock } from '../components/StatsBlock';
import { ProfileHero } from '../components/profile/ProfileHero';
import { HistoryTimeline } from '../components/profile/HistoryTimeline';
import { useHistory } from '../components/profile/useHistory';
import { LinkButton } from '../components/profile/LinkButton';

/**
 * Profil public — même ADN que la page compte, SANS les infos privées :
 * pas d'email, pas de parties en cours. Uniquement ce que l'API publique renvoie.
 */
export function PublicProfilePage() {
  const { displayName = '' } = useParams();
  // Résultat À CLÉ (le pseudo) : changer de profil invalide le résultat par
  // dérivation — pas de reset dans l'effet.
  const [result, setResult] = useState<{
    name: string;
    profile: PublicProfileDto | null;
    notFound: boolean;
  } | null>(null);
  const current = result?.name === displayName ? result : null;
  const profile = current?.profile ?? null;
  const notFound = current?.notFound ?? false;
  // Historique paginé (le hook se réinitialise quand displayName change).
  const history = useHistory(displayName || undefined);

  useEffect(() => {
    let alive = true;
    api
      .publicProfile(displayName)
      .then((p) => alive && setResult({ name: displayName, profile: p, notFound: false }))
      .catch((err) => {
        if (alive)
          setResult({
            name: displayName,
            profile: null,
            notFound: err instanceof ApiError && err.status === 404,
          });
      });
    return () => {
      alive = false;
    };
  }, [displayName]);

  if (notFound)
    return (
      <main className="mx-auto flex max-w-lg flex-col items-center gap-4 px-6 py-20 text-center">
        <Mascot mood="sad" size={96} aria-label="Redpan triste" />
        <p className="font-display text-xl font-bold text-ink-100">Joueur introuvable.</p>
        <p className="text-sm text-ink-400">Ce panda n'habite pas ici…</p>
        <LinkButton to="/" variant="secondary">
          Retour au hub
        </LinkButton>
      </main>
    );
  if (!profile)
    return (
      <main className="grid place-items-center px-6 py-24">
        <Spinner size="lg" label="Chargement du profil…" />
      </main>
    );

  return (
    <main className="mx-auto max-w-content px-4 py-8 sm:px-6 sm:py-12">
      <ProfileHero
        displayName={profile.displayName}
        avatarId={profile.avatarId}
        bio={profile.bio}
        createdAt={profile.createdAt}
      />

      <div className="mt-8 grid gap-8 sm:mt-10 lg:grid-cols-2 lg:items-start">
        <section aria-labelledby="public-stats-title">
          <h2 id="public-stats-title" className="mb-3 font-display text-xl font-bold text-ink-100">
            Stats
          </h2>
          <StatsBlock stats={profile.stats} owner={false} />
        </section>

        <section aria-labelledby="public-history-title">
          <h2
            id="public-history-title"
            className="mb-3 font-display text-xl font-bold text-ink-100"
          >
            Historique
          </h2>
          <HistoryTimeline
            items={history.items}
            error={history.error}
            hasMore={history.hasMore}
            loadingMore={history.loadingMore}
            onLoadMore={history.loadMore}
          />
        </section>
      </div>
    </main>
  );
}
